/**
 * Selleasy FBT + carrito lateral (mismo comportamiento que product-form)
 *
 * - lb-selleasy.js suele añadir al carrito y luego forzar navegación a /cart.
 * - Interceptamos location (href / assign / replace) y además el fetch a cart/add.js?src=selleasy
 *   para disparar cart:change + abrir el drawer (variant:add + force_open_drawer).
 * - En Selleasy puedes dejar "after action" = stay para evitar redirecciones; el parche igual abre el drawer.
 */
(function patchSelleasyCartDrawerIntegration() {
	try {
		if (typeof window === "undefined" || window.__selleasyCartDrawerIntegration) return;
		if (window.themeVariables?.settings?.cartType === "page") return;

		let openDrawerLock = false;

		async function openDrawerAfterSelleasyAdd() {
			if (openDrawerLock) return;
			openDrawerLock = true;
			const root = window.Shopify?.routes?.root || "/";
			try {
				const drawer = document.getElementById("cart-drawer");
				if (!drawer) return;
				const sectionEl = drawer.closest(".shopify-section");
				const sectionId = sectionEl?.id?.replace(/^shopify-section-/, "");
				const cartJson = await (await fetch(`${root}cart.js`)).json();
				if (sectionId) {
					const sectionHtml = await (
						await fetch(`${root}?section_id=${encodeURIComponent(sectionId)}`)
					).text();
					cartJson.sections = cartJson.sections || {};
					cartJson.sections[sectionId] = sectionHtml;
				}
				document.documentElement.dispatchEvent(
					new CustomEvent("cart:change", {
						bubbles: true,
						detail: {
							baseEvent: "variant:add",
							onSuccessDo: "force_open_drawer",
							cart: cartJson,
						},
					})
				);
			} catch (e) {
				console.warn("[selleasy-cart-drawer]", e);
				const drawer = document.getElementById("cart-drawer");
				document.dispatchEvent(new CustomEvent("cart:refresh"));
				if (drawer && typeof drawer.show === "function") {
					const onOnce = () => {
						drawer.removeEventListener("cart-drawer:refreshed", onOnce);
						try {
							drawer.show();
						} catch (err) {
							/* noop */
						}
					};
					drawer.addEventListener("cart-drawer:refreshed", onOnce);
					setTimeout(() => {
						drawer.removeEventListener("cart-drawer:refreshed", onOnce);
						try {
							drawer.show();
						} catch (err) {
							/* noop */
						}
					}, 700);
				}
			} finally {
				setTimeout(() => {
					openDrawerLock = false;
				}, 450);
			}
		}

		function cartPathname() {
			try {
				const root = window.Shopify.routes.root || "/";
				const cartUrl = window.Shopify.routes.cart_url || `${root}cart`;
				return new URL(cartUrl, window.location.origin).pathname.replace(/\/$/, "") || "/";
			} catch {
				return "/cart";
			}
		}

		function isProductPageContext() {
			return (
				document.body?.classList?.contains("template-product") === true ||
				/\/products\//.test(window.location.pathname || "") ||
				!!document.querySelector(
					'.lb-widget-bl, .lb-widget-ba, [class*="lb-widget-"], .shopify-app-block[class*="lb-widget"]'
				)
			);
		}

		function shouldInterceptCartUrl(url) {
			try {
				const u = new URL(url, window.location.origin);
				if (u.origin !== window.location.origin) return false;
				const target = u.pathname.replace(/\/$/, "") || "/";
				const cart = cartPathname();
				if (target !== cart && !target.startsWith(`${cart}/`)) return false;
				return isProductPageContext();
			} catch {
				return false;
			}
		}

		function requestIsSelleasyCartAdd(url) {
			if (!url || typeof url !== "string") return false;
			const lower = url.toLowerCase();
			return (
				lower.includes("/cart/add") &&
				(lower.includes("selleasy") || lower.includes("src=selleasy"))
			);
		}

		let locationPatchApplied = false;
		const applyLocationPatch = () => {
			if (locationPatchApplied) return true;
			if (!window.Shopify?.routes) return false;
			locationPatchApplied = true;

			const hrefDesc = Object.getOwnPropertyDescriptor(Location.prototype, "href");
			if (hrefDesc?.set) {
				const origSet = hrefDesc.set;
				Object.defineProperty(Location.prototype, "href", {
					configurable: true,
					enumerable: hrefDesc.enumerable,
					get: hrefDesc.get,
					set(url) {
						if (shouldInterceptCartUrl(url)) {
							void openDrawerAfterSelleasyAdd();
							return;
						}
						origSet.call(this, url);
					},
				});
			}

			const origAssign = Location.prototype.assign;
			Location.prototype.assign = function (url) {
				if (shouldInterceptCartUrl(url)) {
					void openDrawerAfterSelleasyAdd();
					return;
				}
				return origAssign.call(this, url);
			};

			const origReplace = Location.prototype.replace;
			Location.prototype.replace = function (url) {
				if (shouldInterceptCartUrl(url)) {
					void openDrawerAfterSelleasyAdd();
					return;
				}
				return origReplace.call(this, url);
			};

			return true;
		};

		if (!applyLocationPatch()) {
			const t = setInterval(() => {
				if (applyLocationPatch()) clearInterval(t);
			}, 25);
			setTimeout(() => clearInterval(t), 3000);
		}

		const origFetch = window.fetch;
		window.fetch = function (input, init) {
			const url =
				typeof input === "string"
					? input
					: input instanceof Request
						? input.url
						: "";
			const p = origFetch.apply(this, arguments);
			if (!requestIsSelleasyCartAdd(url)) return p;
			return p.then((response) => {
				if (
					response.ok &&
					isProductPageContext() &&
					window.themeVariables?.settings?.cartType !== "page"
				) {
					queueMicrotask(() => {
						void openDrawerAfterSelleasyAdd();
					});
				}
				return response;
			});
		};

		window.__selleasyCartDrawerIntegration = true;
	} catch (e) {
		console.warn("[selleasy-cart-drawer]", e);
	}
})();

/**
 * Selleasy FBT: evita ir a /cart o checkout y añade con el mismo flujo AJAX que el tema
 * (cart:prepare-bundled-sections + cart/add.js + cart:change) para abrir el cart-drawer.
 */
(function initSelleasyCartDrawer() {
	const ENABLE_SELLEASY_MANUAL_ATC_INTERCEPT = false;
	if (!ENABLE_SELLEASY_MANUAL_ATC_INTERCEPT) return;

	const HOST = ".selleasy-widget-host[data-selleasy-fbt-host], .selleasy-widget-host";

	function getHost(el) {
		return el?.closest?.(HOST) ?? null;
	}

	function collectLineItems(host) {
		const items = [];
		const seen = new Set();
		const add = (id, qty) => {
			const n = parseInt(String(id), 10);
			if (!n || seen.has(n)) return;
			seen.add(n);
			const q = Math.max(1, parseInt(String(qty), 10) || 1);
			items.push({ id: n, quantity: q });
		};

		const rows = host.querySelectorAll(
			'[class*="lb-"], [class*="lb_"], [data-lb-product], tr[data-product-id], [data-product-id]'
		);
		if (rows.length) {
			rows.forEach((row) => {
				const cb = row.querySelector('input[type="checkbox"]');
				if (cb && !cb.checked) return;
				row.querySelectorAll('select[name="id"], select[name*="variant"]').forEach((sel) => {
					if (sel.value) add(sel.value);
				});
				const hid = row.querySelector('input[type="hidden"][name="id"]');
				if (hid?.value) add(hid.value);
			});
		}
		if (items.length) return items;

		host.querySelectorAll("[data-variant-id]").forEach((el) => {
			const row = el.closest('tr, li, [data-product-id], [class*="lb-"]') || host;
			const cb = row.querySelector?.('input[type="checkbox"]');
			if (cb && !cb.checked) return;
			add(el.getAttribute("data-variant-id"));
		});
		host.querySelectorAll('input[name="id"][type="hidden"]').forEach((inp) => {
			if (inp.value) add(inp.value);
		});
		host.querySelectorAll('select[name="id"]').forEach((sel) => {
			if (sel.value) add(sel.value);
		});

		return items;
	}

	async function postCartAddForm(form) {
		const sectionsToBundle = [];
		document.documentElement.dispatchEvent(
			new CustomEvent("cart:prepare-bundled-sections", {
				bubbles: true,
				detail: { sections: sectionsToBundle },
			})
		);
		const fd = new FormData(form);
		fd.set("sections", sectionsToBundle.join(","));
		const vid = fd.get("id");
		if (vid) {
			fd.set("sections_url", `${window.Shopify.routes.root}variants/${vid}`);
		}
		document.documentElement.dispatchEvent(
			new CustomEvent("theme:loading:start", { bubbles: true })
		);
		const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
			method: "POST",
			body: fd,
			headers: { "X-Requested-With": "XMLHttpRequest" },
		});
		document.documentElement.dispatchEvent(
			new CustomEvent("theme:loading:end", { bubbles: true })
		);
		const responseJson = await response.json();
		if (!response.ok) {
			document.dispatchEvent(new CustomEvent("cart:refresh"));
			return;
		}
		const cartContent = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
		cartContent.sections = responseJson.sections;
		document.documentElement.dispatchEvent(
			new CustomEvent("cart:change", {
				bubbles: true,
				detail: {
					baseEvent: "variant:add",
					onSuccessDo: fd.get("on_success") || undefined,
					cart: cartContent,
				},
			})
		);
	}

	async function postCartAddItemsJson(items) {
		const sectionsToBundle = [];
		document.documentElement.dispatchEvent(
			new CustomEvent("cart:prepare-bundled-sections", {
				bubbles: true,
				detail: { sections: sectionsToBundle },
			})
		);
		document.documentElement.dispatchEvent(
			new CustomEvent("theme:loading:start", { bubbles: true })
		);
		const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"X-Requested-With": "XMLHttpRequest",
			},
			body: JSON.stringify({
				items,
				sections: sectionsToBundle.join(","),
				sections_url: `${window.Shopify.routes.root}variants/${items[0].id}`,
			}),
		});
		document.documentElement.dispatchEvent(
			new CustomEvent("theme:loading:end", { bubbles: true })
		);
		const responseJson = await response.json();
		if (!response.ok) {
			document.dispatchEvent(new CustomEvent("cart:refresh"));
			return;
		}
		const cartContent = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
		cartContent.sections = responseJson.sections;
		document.documentElement.dispatchEvent(
			new CustomEvent("cart:change", {
				bubbles: true,
				detail: {
					baseEvent: "variant:add",
					cart: cartContent,
				},
			})
		);
	}

	function isCartOrCheckoutHref(href) {
		if (!href || href === "#") return false;
		try {
			const u = new URL(href, window.location.origin);
			return (
				u.pathname.includes("/cart") ||
				u.pathname.includes("/checkout")
			);
		} catch {
			return false;
		}
	}

	function looksLikeSelleasyAction(el) {
		if (!el || el.closest?.(".share-button, share-button, [data-wishlist]")) return false;
		if (el.matches?.('a[href*="/cart"], a[href*="/checkout"]')) return isCartOrCheckoutHref(el.getAttribute("href"));
		const cls = (el.className && String(el.className).toLowerCase()) || "";
		if (/(lb-button|lb-button-bundle|lb-widget)/i.test(cls) || el.closest?.(".lb-button-bundle")) return true;
		if (el.matches?.("button, [role='button']")) {
			const text = (el.textContent || "").toLowerCase();
			const aria = (el.getAttribute("aria-label") || "").toLowerCase();
			if (
				/(carrito|cart|checkout|agregar|add to|look|bundle|combo|comprar)/i.test(text) ||
				/(carrito|cart|add|bundle|look)/i.test(aria) ||
				/(lb-|lb_|add|bundle|btn-cart|upsell|selleasy)/i.test(cls)
			) {
				return true;
			}
		}
		return false;
	}

	document.addEventListener(
		"submit",
		(e) => {
			const form = e.target;
			if (!(form instanceof HTMLFormElement)) return;
			const host = getHost(form);
			if (!host || !form.action?.includes?.("/cart/add")) return;
			e.preventDefault();
			e.stopPropagation();
			postCartAddForm(form);
		},
		true
	);

	document.addEventListener(
		"click",
		(e) => {
			const t = e.target;
			const el = t.closest?.('a, button, [role="button"]');
			if (!el) return;
			const host = getHost(el);
			if (!host) return;

			/* Selleasy: <div role="button" class="lb-button"> — el ATC lo hace lb-selleasy.js; aquí no interceptamos
			   el clic; patchSelleasyForcedCartRedirect evita la redirección a /cart. */
			if (el.matches?.(".lb-button") || el.closest?.(".lb-button-cont")) {
				return;
			}

			if (!looksLikeSelleasyAction(el)) return;

			if (el.matches("a")) {
				const href = el.getAttribute("href");
				if (!isCartOrCheckoutHref(href)) return;
				const items = collectLineItems(host);
				if (!items.length) return;
				e.preventDefault();
				e.stopPropagation();
				postCartAddItemsJson(items);
				return;
			}

			if (el.matches("button") && el.type === "submit") {
				const form = el.closest("form");
				if (form?.action?.includes?.("/cart/add")) return;
			}

			if (el.matches("button") || el.matches?.('[role="button"]')) {
				const items = collectLineItems(host);
				if (!items.length) return;
				e.preventDefault();
				e.stopImmediatePropagation();
				postCartAddItemsJson(items);
			}
		},
		true
	);
})();

/**
 * BOGOS: el pop-up de regalos debe ser lo único interactuable mientras está abierto.
 *
 * Problema: si un drawer del tema (menú mobile #sidebar-menu, carrito lateral, etc.) está abierto,
 * su focus-trap trata cualquier toque en el pop-up como "click afuera": cancela el touchstart
 * (el botón AÑADIR nunca recibe el click), cierra el drawer y le roba el foco al pop-up.
 *
 * Solución, mientras #freegifts-main-popup-container tenga "sca-is-visible-fg":
 * - Se pausa el focus-trap de los drawers abiertos (o que se abran) y se reanuda al cerrar.
 * - Un click/toque dentro del pop-up nunca cierra un drawer del tema.
 * - El resto del <body> se marca como inert (sin clicks, toques ni foco).
 */
(function lockPageWhileBogosPopupOpen() {
	if (typeof window === "undefined" || window.__bogosPopupLock) return;
	window.__bogosPopupLock = true;

	const POPUP_SELECTOR = "#freegifts-main-popup-container.sca-modal-fg";
	const VISIBLE_CLASS = "sca-is-visible-fg";
	const LOCK_CLASS = "bogos-popup-open";
	const DIALOG_SELECTOR = "[open]";
	let inertedElements = [];
	let pausedTraps = new Set();
	let observedPopup = null;

	const getPopup = () => document.querySelector(POPUP_SELECTOR);
	const isPopupVisible = () => getPopup()?.classList.contains(VISIBLE_CLASS) ?? false;
	const isInsidePopup = (target) => !!(target instanceof Element && target.closest(POPUP_SELECTOR));
	const isLocked = () => document.documentElement.classList.contains(LOCK_CLASS);

	// Evita que un click dentro del pop-up cierre un drawer del tema (DialogElement de theme.js)
	function patchDialog(dialog) {
		if (dialog.__bogosPatched || typeof dialog.hideForOutsideClickTarget !== "function") return;
		dialog.__bogosPatched = true;
		const originalHide = dialog.hideForOutsideClickTarget;
		const originalAllow = dialog.allowOutsideClickForTarget;
		dialog.hideForOutsideClickTarget = function (target) {
			return isInsidePopup(target) ? false : originalHide.call(this, target);
		};
		dialog.allowOutsideClickForTarget = function (target) {
			return isInsidePopup(target) || (originalAllow ? originalAllow.call(this, target) : false);
		};
	}

	function pauseDialogTrap(dialog) {
		if (!dialog.open || !("focusTrap" in dialog)) return;
		patchDialog(dialog);
		const trap = dialog.focusTrap;
		if (trap?.active && !trap.paused) {
			trap.pause();
			pausedTraps.add(trap);
		}
	}

	function lock(popup) {
		document.documentElement.classList.add(LOCK_CLASS);
		document.querySelectorAll(DIALOG_SELECTOR).forEach(pauseDialogTrap);

		// Marca como inert a los hermanos de cada ancestro del pop-up, sin tocar la ruta hasta él
		for (let node = popup; node && node !== document.body; node = node.parentElement) {
			const parent = node.parentElement;
			if (!parent) break;
			for (const sibling of parent.children) {
				if (sibling === node || sibling.inert || /^(SCRIPT|STYLE|LINK)$/.test(sibling.tagName)) continue;
				sibling.inert = true;
				inertedElements.push(sibling);
			}
		}
	}

	function unlock() {
		inertedElements.forEach((el) => (el.inert = false));
		inertedElements = [];
		pausedTraps.forEach((trap) => trap.active && trap.paused && trap.unpause());
		pausedTraps = new Set();
		document.documentElement.classList.remove(LOCK_CLASS);
	}

	function sync() {
		const popup = getPopup();
		const visible = !!popup && popup.classList.contains(VISIBLE_CLASS);
		if (visible && !isLocked()) lock(popup);
		else if (!visible && isLocked()) unlock();
	}

	const popupObserver = new MutationObserver(sync);

	function watchPopup() {
		const popup = getPopup();
		if (popup === observedPopup) return;
		popupObserver.disconnect();
		observedPopup = popup;
		if (popup) popupObserver.observe(popup, { attributes: true, attributeFilter: ["class"] });
		sync();
	}

	// BOGOS inyecta el pop-up después de cargar; esperamos a que aparezca (o se re-renderice)
	new MutationObserver(watchPopup).observe(document.body, { childList: true, subtree: true });
	watchPopup();

	// Si un drawer se abre con el pop-up visible (p. ej. el carrito tras añadir un regalo),
	// su focus-trap también se pausa. "toggle" se dispara después de activar el trap.
	document.addEventListener("toggle", (event) => {
		if (isLocked() && event.target instanceof Element && event.target.open) pauseDialogTrap(event.target);
	});
})();
